import React, { createContext, useContext, useMemo, useReducer } from 'react'
import { CART_ELEMENTS_TYPE, CART_SCROLLABLE_ELEMENTS_TYPE } from 'src/components/cartV2/config'

export type CartElementType = CART_ELEMENTS_TYPE | CART_SCROLLABLE_ELEMENTS_TYPE

export interface PreviewCartItem {
  id: string
  title: string
  variant: string
  price: number
  originalPrice: number
  quantity: number
  is_freebie?: boolean
}

interface CartLayoutStateType {
  selectedElement: CartElementType | null
  selectedFromPreview: boolean
  cartItems: PreviewCartItem[]
}

enum ActionType {
  SET_SELECTED_ELEMENT = 'SET_SELECTED_ELEMENT',
  UPDATE_CART_ITEM = 'UPDATE_CART_ITEM'
}

type CartLayoutAction =
  | { type: ActionType.SET_SELECTED_ELEMENT; payload: CartElementType | null; fromPreview?: boolean }
  | { type: ActionType.UPDATE_CART_ITEM; payload: PreviewCartItem }

interface CartLayoutContextType {
  state: CartLayoutStateType
  actions: {
    setSelectedElement: (payload: CartElementType | null, options?: { fromPreview?: boolean }) => void
    updateCartItem: (item: PreviewCartItem) => void
  }
  getters: {
    selectedElement: CartElementType | null
    selectedFromPreview: boolean
    cartItems: PreviewCartItem[]
  }
}

const initialState: CartLayoutStateType = {
  selectedElement: null,
  selectedFromPreview: false,
  cartItems: []
}

const CartLayoutContext = createContext<CartLayoutContextType | undefined>(undefined)

const reducer = (state: CartLayoutStateType, action: CartLayoutAction): CartLayoutStateType => {
  switch (action.type) {
    case ActionType.SET_SELECTED_ELEMENT: {
      const fromPreview = action.fromPreview ?? false
      return {
        ...state,
        selectedElement: action.payload,
        selectedFromPreview: fromPreview
      }
    }
    case ActionType.UPDATE_CART_ITEM: {
      const existingIndex = state.cartItems.findIndex(item => item.id === action.payload.id)
      if (existingIndex >= 0) {
        const updatedItems = [...state.cartItems]
        updatedItems[existingIndex] = action.payload
        return { ...state, cartItems: updatedItems }
      }
      return { ...state, cartItems: [...state.cartItems, action.payload] }
    }
    default:
      return state
  }
}

export const CartLayoutProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, initialState)

  const setSelectedElement = (payload: CartElementType | null, options?: { fromPreview?: boolean }) => {
    dispatch({ type: ActionType.SET_SELECTED_ELEMENT, payload, fromPreview: options?.fromPreview })
  }

  const updateCartItem = (item: PreviewCartItem) => {
    dispatch({ type: ActionType.UPDATE_CART_ITEM, payload: item })
  }

  const contextValue = useMemo<CartLayoutContextType>(
    () => ({
      state,
      actions: {
        setSelectedElement,
        updateCartItem
      },
      getters: {
        selectedElement: state.selectedElement,
        selectedFromPreview: state.selectedFromPreview,
        cartItems: state.cartItems
      }
    }),
    [state]
  )

  return <CartLayoutContext.Provider value={contextValue}>{children}</CartLayoutContext.Provider>
}

export const useCartLayoutContext = () => {
  const context = useContext(CartLayoutContext)
  if (!context) {
    throw new Error('useCartLayoutContext must be used within a CartLayoutProvider')
  }
  return context
}
